<script setup>
import { ref, watch, getCurrentInstance } from 'vue'
import { i18n } from '@/i18n'
import { getReservedStockHistory, downloadByUrl } from '@/api/reserved-stock'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['close'])

const loading = ref(false)
const historyList = ref([])
const totalRow = ref(0)
const page = ref(1)
const size = ref(10)

watch(() => props.visible, (val) => {
  if (val) {
    page.value = 1
    fetchHistory()
  }
})

async function fetchHistory() {
  if (!props.item?.catalog_id || !props.item?.warehouse_id) return

  loading.value = true
  try {
    const { data } = await getReservedStockHistory({
      catalog_id: props.item.catalog_id,
      warehouse_id: props.item.warehouse_id,
      business_id: props.item.business_id,
      page: page.value,
      size: size.value,
    })

    const rawData = data?.content || data?.data || []
    historyList.value = rawData.map(record => {
      const prev = Number(record.previous_reserved_stock ?? record.previous_reserved ?? 0)
      const next = Number(record.new_reserved_stock ?? record.new_reserved ?? 0)
      
      return {
        ...record,
        previous_reserved: prev,
        new_reserved: next,
        movement_type: record.movement_type || (next > prev ? 'RESERVED_IN' : 'RESERVED_OUT'),
        quantity: record.quantity ?? Math.abs(next - prev),
      }
    })
    totalRow.value = data?.total_of_elements ?? data?.total_row ?? 0
  } catch {
    historyList.value = []
    totalRow.value = 0
  } finally {
    loading.value = false
  }
}

function onPageChange(newPage) {
  page.value = newPage
  fetchHistory()
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  const pad = (n) => String(n).padStart(2, '0')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${pad(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const { $notification } = getCurrentInstance().proxy

function copyToClipboard(text) {
  if (!text) return
  navigator.clipboard.writeText(text).then(() => {
    $notification.success({
      message: i18n.t('productAvailability.copy_success', { value: text }) || `Copied ${text}`,
    })
  })
}

async function downloadFile(record) {
  const url = record?.file_url
  if (!url) return

  try {
    const response = await downloadByUrl(url)
    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    
    // 1. Try to get filename from content-disposition header (Server Choice)
    let filename = ''
    const disposition = response.headers['content-disposition']
    if (disposition && disposition.includes('filename=')) {
      filename = disposition.split('filename=')[1].replace(/['"]/g, '')
    }
    
    // 2. Fallback jika server tidak mengirimkan nama file
    if (!filename) {
      filename = 'Result_ReservedStock.xlsx'
    }

    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)
  } catch (err) {
    console.error('Download failed', err)
    $notification.error({
      message: 'Download Failed',
      description: 'Could not download the file. Please try again later.',
    })
  }
}
</script>

<template>
  <a-drawer
    :visible="visible"
    :title="null"
    placement="right"
    width="480"
    :destroy-on-close="true"
    :header-style="{ display: 'none' }"
    :body-style="{ padding: 0 }"
    @close="$emit('close')"
  >
    <!-- Custom Header -->
    <div class="drawer-hero">
      <div class="drawer-hero__top">
        <div class="drawer-hero__label">
          <a-icon type="history" />
          {{ $t('productAvailability.reserved_stock_history') }}
        </div>
        <button class="drawer-hero__close" @click="$emit('close')">
          <a-icon type="close" />
        </button>
      </div>
      <div class="drawer-hero__product">
        {{ item.title }}
      </div>
      <div class="drawer-hero__meta">
        <span class="pill pill--sku" @click="copyToClipboard(item.sku)">
          <span class="pill__label">SKU</span>
          <span class="pill__value">{{ item.sku }}</span>
          <a-icon type="copy" class="pill__icon" />
        </span>
        <span v-if="item.warehouse_name" class="pill pill--wh">
          <a-icon type="environment" />
          {{ item.warehouse_name }}
        </span>
      </div>
    </div>

    <!-- Body -->
    <div class="drawer-body">
      <!-- Loading -->
      <div v-if="loading" class="drawer-body__loading">
        <a-spin />
      </div>

      <!-- Empty -->
      <a-empty
        v-else-if="!historyList.length"
        class="py-5"
        :description="$t('productAvailability.reserved_stock_no_history')"
      />

      <!-- Timeline -->
      <div v-else class="timeline">
        <div
          v-for="(record, idx) in historyList"
          :key="record.id || idx"
          class="timeline-card"
          :class="{ 'timeline-card--in': record.movement_type === 'RESERVED_IN' }"
        >
          <div class="timeline-card__accent" />

          <div class="timeline-card__content">
            <!-- Row 1: Tag + Date -->
            <div class="timeline-card__top">
              <span
                class="badge"
                :class="record.movement_type === 'RESERVED_IN' ? 'badge--in' : 'badge--out'"
              >
                {{ record.movement_type === 'RESERVED_IN'
                  ? $t('productAvailability.reserved_in')
                  : $t('productAvailability.reserved_out')
                }}
              </span>
              <span class="timeline-card__date">{{ formatDate(record.created_at) }}</span>
            </div>

            <!-- Row 2: Values -->
            <div class="timeline-card__values">
              <span class="val val--prev">{{ record.previous_reserved }}</span>
              <a-icon type="swap-right" class="val__arrow" />
              <span class="val val--next">{{ record.new_reserved }}</span>
              <span
                v-if="record.quantity > 0"
                class="val__delta"
                :class="record.movement_type === 'RESERVED_IN' ? 'val__delta--in' : 'val__delta--out'"
              >
                {{ record.movement_type === 'RESERVED_IN' ? '+' : '-' }}{{ record.quantity }}
              </span>
            </div>

            <!-- Row 3: Reason -->
            <div v-if="record.reason" class="timeline-card__reason">
              {{ record.reason }}
            </div>

            <!-- Row 4: Footer -->
            <div class="timeline-card__footer">
              <span class="timeline-card__user">
                <a-icon type="user" />
                {{ record.user_name }}
              </span>

              <span v-if="record.file_url" class="timeline-card__upload">
                <a-icon type="cloud-upload" />
                {{ $t('productAvailability.reserved_stock_from_upload') }}
                <a href="#" class="timeline-card__dl" @click.prevent="downloadFile(record)">
                  <a-icon type="download" />
                  {{ $t('productAvailability.reserved_stock_download_file') }}
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalRow > size" class="drawer-body__pagination">
        <a-pagination
          :current="page"
          :page-size="size"
          :total="totalRow"
          size="small"
          @change="onPageChange"
        />
      </div>
    </div>
  </a-drawer>
</template>

<style lang="scss" scoped>
/* ─── Hero Header ─── */
.drawer-hero {
  background: #ffffff;
  padding: 20px 24px 18px;
  color: #262626;
  border-bottom: 1px solid #eef0f3;

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #8c8c8c;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__close {
    background: rgba(0, 0, 0, 0.05);
    border: none;
    color: #595959;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s;
    font-size: 14px;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }

  &__product {
    font-size: 16px;
    font-weight: 600;
    line-height: 1.4;
    margin-bottom: 14px;
    color: #262626;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
}

/* ─── Pills ─── */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;

  &--sku {
    background: #f6ffed;
    color: #52c41a;
    border: 1px solid #b7eb8f;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #f4ffe3;
      border-color: #73d13d;
    }

    .pill__value {
      color: #1b4e07;
    }
  }

  &--wh {
    background: #e6f7ff;
    color: #1890ff;
    border: 1px solid #91d5ff;
  }

  &__label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    opacity: 0.7;
  }

  &__value {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-weight: 600;
  }

  &__icon {
    font-size: 10px;
    opacity: 0.7;
  }
}

/* ─── Body ─── */
.drawer-body {
  padding: 20px;
  min-height: calc(100vh - 180px);
  background: #f5f6fa;

  &__loading {
    display: flex;
    justify-content: center;
    padding: 60px 0;
  }

  &__pagination {
    margin-top: 16px;
    text-align: right;
    padding-top: 12px;
  }
}

/* ─── Timeline Cards ─── */
.timeline {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.timeline-card {
  display: flex;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  &__accent {
    width: 4px;
    flex-shrink: 0;
    background: #52c41a;
  }

  &--in .timeline-card__accent {
    background: #fa8c16;
  }

  &__content {
    flex: 1;
    padding: 12px 14px;
    min-width: 0;
  }

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__date {
    font-size: 11px;
    color: #8c8c8c;
  }

  &__values {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__reason {
    font-size: 12px;
    color: #595959;
    padding: 6px 10px;
    background: #fafafa;
    border-radius: 6px;
    border-left: 2px solid #e0e0e0;
    margin-bottom: 8px;
    line-height: 1.4;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding-top: 10px;
    margin-top: 8px;
    background-image: linear-gradient(to right, #d9d9d9 33%, rgba(255, 255, 255, 0) 0%);
    background-position: top;
    background-size: 12px 1px;
    background-repeat: repeat-x;
    font-size: 11px;
    color: #8c8c8c;
  }

  &__user {
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
    color: #595959;
    max-width: 180px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__upload {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    color: #8c8c8c;
    margin-left: auto;
  }

  &__dl {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    color: #667eea;
    font-weight: 600;
    margin-left: 4px;

    &:hover {
      color: #764ba2;
      text-decoration: underline;
    }
  }
}

/* ─── Badge ─── */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;

  &--out {
    background: #f0faf0;
    color: #389e0d;
  }

  &--in {
    background: #fff7e6;
    color: #d46b08;
  }
}

/* ─── Values ─── */
.val {
  font-size: 18px;
  font-weight: 700;

  &--prev {
    color: #bfbfbf;
    font-weight: 400;
    font-size: 16px;
  }

  &--next {
    color: #262626;
  }

  &__arrow {
    font-size: 14px;
    color: #d9d9d9;
  }

  &__delta {
    font-size: 12px;
    font-weight: 700;
    padding: 1px 8px;
    border-radius: 12px;
    margin-left: 4px;

    &--in {
      background: #fff7e6;
      color: #d46b08;
    }

    &--out {
      background: #f0faf0;
      color: #389e0d;
    }
  }
}
</style>
